import React, {
  ChangeEvent,
  FunctionComponent,
  useEffect,
} from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MoreHoriz } from '@mui/icons-material';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { format } from 'date-fns';
import { trimStart } from 'lodash';
import { useSnackbar } from 'notistack';
import { ColDef, ICellRendererParams } from 'ag-grid-community';

import * as API from '../../services/API';
import { ModelInstance } from '../../types/models';

import useSetState from '../../hooks/useSetState';
import { useWindowDimensions } from '../../hooks/useWindowDimensions';
import PopupDialog from '../shared/PopupDialog';
import StyledMenu from '../shared/StyledMenu';
import Table from '../shared/Table';
import OverflowTooltip from '../shared/OverflowTooltip';
import CenteredSpinner from '../shared/CenteredSpinner';

const TAG_NAME_LENGTH = 100;
const DESCRIPTION_LENGTH = 500;

interface ScenarioTag {
  id: number;
  name: string;
  description?: string;
  client_id: number;
  updated_at: string;
  updated_by?: {
    Display_Name: string;
    User_Name: string;
  };
  updated_by_id?: number;
}

interface IManageScenarioTagsProps {
  isManageScenarioTagsDialogOpen: boolean;
  handleCloseManageScenarioTagsDialog: () => void;
  modelInstanceId?: ModelInstance['id'];
  clientId?: number;
  onTagsChanged?: () => void;
}

interface IManageScenarioTagsState {
  isAddTagModalOpen: boolean;
  isEditTagModalOpen: boolean;
  showDeleteTagModal: boolean;
  isDataLoading: boolean;
  isModalLoading: boolean;
  scenarioTags: ScenarioTag[];
  selectedTag: ScenarioTag | null;
  tagName: string;
  tagDescription: string;
  onFocusedInput?: string;
  deleteConfirmationName: string;
}

const initialState: IManageScenarioTagsState = {
  isAddTagModalOpen: false,
  isEditTagModalOpen: false,
  showDeleteTagModal: false,
  isDataLoading: false,
  isModalLoading: false,
  scenarioTags: [],
  selectedTag: null,
  tagName: '',
  tagDescription: '',
  onFocusedInput: undefined,
  deleteConfirmationName: '',
};

const ManageScenarioTags: FunctionComponent<IManageScenarioTagsProps> = (
  props: IManageScenarioTagsProps
) => {
  const [state, setState] = useSetState<IManageScenarioTagsState>(initialState);
  const { enqueueSnackbar } = useSnackbar();
  const { width } = useWindowDimensions();

  useEffect(() => {
    if (props.isManageScenarioTagsDialogOpen && props.modelInstanceId) {
      loadTags();
    }
  }, [props.isManageScenarioTagsDialogOpen, props.modelInstanceId]);

  const loadTags = async (): Promise<void> => {
    setState({ isDataLoading: true });

    try {
      const response = await API.load(`/clients/${props.clientId}/scenario_tags`);
      // Map API response (PascalCase) to UI format (lowercase)
      const tags = (response || []).map((tag: any) => ({
        id: tag.id,
        name: tag.Name,
        description: tag.Description,
        client_id: tag.ClientID,
        updated_at: tag.updated_at,
        updated_by: tag.updated_by,
        updated_by_id: tag.updated_by_id,
      }));
      setState({
        scenarioTags: tags,
        isDataLoading: false,
      });
    } catch (error) {
      setState({
        scenarioTags: [],
        isDataLoading: false,
      });
      enqueueSnackbar('Failed to load tags', { variant: 'error' });
    }
  };

  const handleAddTagModalOpen = (): void => {
    setState({
      isAddTagModalOpen: true,
      tagName: '',
      tagDescription: '',
    });
  };

  const handleAddTagModalClose = (): void => {
    setState({
      isAddTagModalOpen: false,
      tagName: '',
      tagDescription: '',
    });
  };

  const handleEditTagModalOpen = (tag: ScenarioTag): void => {
    setState({
      isEditTagModalOpen: true,
      selectedTag: tag,
      tagName: tag.name,
      tagDescription: tag.description || '',
    });
  };

  const handleEditTagModalClose = (): void => {
    setState({
      isEditTagModalOpen: false,
      selectedTag: null,
      tagName: '',
      tagDescription: '',
    });
  };

  const handleDeleteTagModalOpen = (tag: ScenarioTag): void => {
    setState({
      showDeleteTagModal: true,
      selectedTag: tag,
    });
  };

  const handleDeleteTagModalClose = (): void => {
    setState({
      showDeleteTagModal: false,
      selectedTag: null,
      deleteConfirmationName: '',
    });
  };

  const handleAddTag = async (): Promise<void> => {
    if (!state.tagName.trim()) {
      enqueueSnackbar('Tag name is required', { variant: 'error' });
      return;
    }
    if (!state.tagDescription.trim()) {
      enqueueSnackbar('Description is required', { variant: 'error' });
      return;
    }

    setState({ isModalLoading: true });

    try {
      await API.create(`/clients/${props.clientId}/scenario_tags`, {
        name: state.tagName.trim(),
        description: state.tagDescription.trim(),
      });
      
      // Only proceed if API call was successful
      setState({ isModalLoading: false });
      handleAddTagModalClose();
      enqueueSnackbar('Tag created successfully', { variant: 'success' });
      
      // Notify parent that tags have changed
      if (props.onTagsChanged) {
        props.onTagsChanged();
      }
      
      // Small delay to ensure modal closes before reloading data
      setTimeout(() => {
        loadTags();
      }, 100);
    } catch (error: any) {
      setState({ isModalLoading: false });
      enqueueSnackbar(
        error?.body?.message || 'Failed to create tag',
        { variant: 'error' }
      );
      // Modal stays open on error, table doesn't reload
    }
  };

  const handleEditTag = async (): Promise<void> => {
    if (!state.tagName.trim() || !state.selectedTag) {
      enqueueSnackbar('Tag name is required', { variant: 'error' });
      return;
    }
    if (!state.tagDescription.trim()) {
      enqueueSnackbar('Description is required', { variant: 'error' });
      return;
    }

    setState({ isModalLoading: true });

    try {
      await API.update(
        `/clients/${props.clientId}/scenario_tags/${state.selectedTag.id}`,
        {
          name: state.tagName.trim(),
          description: state.tagDescription.trim(),
        }
      );
      
      setState({ isModalLoading: false });
      handleEditTagModalClose();
      enqueueSnackbar('Tag updated successfully', { variant: 'success' });
      
      // Notify parent that tags have changed
      if (props.onTagsChanged) {
        props.onTagsChanged();
      }
      
      // Small delay to ensure modal closes before reloading data
      setTimeout(() => {
        loadTags();
      }, 100);
    } catch (error: any) {
      setState({ isModalLoading: false });
      enqueueSnackbar(
        error?.body?.message || 'Failed to update tag',
        { variant: 'error' }
      );
      // Modal stays open on error, table doesn't reload
    }

    // Temporary: Simulate success (commented for reference)
    // setTimeout(() => {
    //   setState({ isModalLoading: false });
    //   handleEditTagModalClose();
    //   enqueueSnackbar('Tag updated successfully (dummy)', {
    //     variant: 'success',
    //   });
    //   loadTags();
    // }, 500);
  };

  const handleDeleteTag = async (): Promise<void> => {
    if (!state.selectedTag) return;

    setState({ isModalLoading: true });

    try {
      await API.remove(
        `/clients/${props.clientId}/scenario_tags/${state.selectedTag.id}`
      );
      
      setState({ isModalLoading: false });
      handleDeleteTagModalClose();
      enqueueSnackbar('Tag deleted successfully', { variant: 'success' });
      
      // Notify parent that tags have changed
      if (props.onTagsChanged) {
        props.onTagsChanged();
      }
      
      // Small delay to ensure modal closes before reloading data
      setTimeout(() => {
        loadTags();
      }, 100);
    } catch (error: any) {
      setState({ isModalLoading: false });
      enqueueSnackbar(
        error?.body?.message || 'Failed to delete tag',
        { variant: 'error' }
      );
      // Modal stays open on error, table doesn't reload
    }
  };

  const dateFormatter = (
    params: ICellRendererParams<ScenarioTag>
  ): JSX.Element => {
    if (params.value) {
      return (
        <OverflowTooltip
          {...params}
          value={format(new Date(params.value), 'MM/dd/yyyy hh:mm:ss a')}
        />
      );
    }
    return <Typography>-</Typography>;
  };

  const lastEditedByFormatter = (
    params: ICellRendererParams<ScenarioTag>
  ): JSX.Element => {
    if (params.data?.updated_by?.Display_Name) {
      return (
        <Tooltip
          title={params.data.updated_by.User_Name}
          arrow
          placement="bottom-start"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -10],
                  },
                },
              ],
            },
          }}
        >
          <Typography
            component="span"
            noWrap
            variant="body2"
            sx={{
              lineHeight: 'inherit',
              color: 'primary.main',
              textDecoration: 'underline',
              display: 'inline-block',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {params.data.updated_by.Display_Name}
          </Typography>
        </Tooltip>
      );
    }
    return <Typography>-</Typography>;
  };

  const actionFormatter = (
    params: ICellRendererParams<ScenarioTag>
  ): JSX.Element => {
    return (
      <PopupState variant="popover" popupId="manage-scenario-tags-menu">
        {(popupState) => (
          <>
            <IconButton {...bindTrigger(popupState)}>
              <MoreHoriz />
            </IconButton>
            <StyledMenu {...bindMenu(popupState)}>
              <MenuItem
                onClick={() => {
                  popupState.close();
                  handleEditTagModalOpen(params.data as ScenarioTag);
                }}
              >
                Edit
              </MenuItem>
              <MenuItem
                onClick={() => {
                  popupState.close();
                  handleDeleteTagModalOpen(params.data as ScenarioTag);
                }}
              >
                Delete
              </MenuItem>
            </StyledMenu>
          </>
        )}
      </PopupState>
    );
  };

  const scenarioTagsColumns: ColDef[] = [
    {
      field: 'name',
      headerName: 'Tag Name',
      cellRenderer: OverflowTooltip,
      sortable: true,
      sort: 'asc',
      flex: 2,
    },
    {
      field: 'description',
      headerName: 'Description',
      cellRenderer: OverflowTooltip,
      sortable: true,
      flex: 3,
    },
    {
      field: 'updated_at',
      headerName: 'Last Edited At',
      cellRenderer: dateFormatter,
      sortable: true,
      flex: 2,
    },
    {
      field: 'updated_by.Display_Name',
      headerName: 'Last Edited By',
      cellRenderer: lastEditedByFormatter,
      sortable: true,
      flex: 2,
    },
    {
      field: 'id',
      headerName: 'Actions',
      cellRenderer: actionFormatter,
      flex: 1,
    },
  ];

  return (
    <>
      {/* Add Tag Modal */}
      <PopupDialog
        open={state.isAddTagModalOpen}
        title="Add Scenario Tag"
        close={handleAddTagModalClose}
        primaryButtons={[
          {
            id: 'add-tag-button',
            label: 'Add',
            onClick: handleAddTag,
            disabled:
              !state.tagName.trim() ||
              !state.tagDescription.trim() ||
              state.isModalLoading,
          },
        ]}
      >
        <Box>
          <TextField
            fullWidth
            label="Tag Name"
            required
            size="small"
            helperText={
              state.onFocusedInput === 'tagName' &&
              `${TAG_NAME_LENGTH - state.tagName.length} characters left`
            }
            onFocus={() => setState({ onFocusedInput: 'tagName' })}
            onBlur={() => setState({ onFocusedInput: undefined })}
            value={state.tagName}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setState({
                tagName: trimStart(
                  e.target.value.substring(0, TAG_NAME_LENGTH)
                ),
              })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            required
            multiline
            rows={3}
            size="small"
            helperText={
              state.onFocusedInput === 'description' &&
              `${DESCRIPTION_LENGTH - state.tagDescription.length} characters left`
            }
            onFocus={() => setState({ onFocusedInput: 'description' })}
            onBlur={() => setState({ onFocusedInput: undefined })}
            value={state.tagDescription}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setState({
                tagDescription: e.target.value.substring(0, DESCRIPTION_LENGTH),
              })
            }
          />
        </Box>
      </PopupDialog>

      {/* Edit Tag Modal */}
      <PopupDialog
        open={state.isEditTagModalOpen}
        title="Edit Scenario Tag"
        close={handleEditTagModalClose}
        primaryButtons={[
          {
            id: 'edit-tag-button',
            label: 'Save',
            onClick: handleEditTag,
            disabled:
              !state.tagName.trim() ||
              !state.tagDescription.trim() ||
              state.isModalLoading,
          },
        ]}
      >
        <Box>
          <TextField
            fullWidth
            label="Tag Name"
            required
            size="small"
            helperText={
              state.onFocusedInput === 'tagName' &&
              `${TAG_NAME_LENGTH - state.tagName.length} characters left`
            }
            onFocus={() => setState({ onFocusedInput: 'tagName' })}
            onBlur={() => setState({ onFocusedInput: undefined })}
            value={state.tagName}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setState({
                tagName: trimStart(
                  e.target.value.substring(0, TAG_NAME_LENGTH)
                ),
              })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            required
            multiline
            rows={3}
            size="small"
            helperText={
              state.onFocusedInput === 'description' &&
              `${DESCRIPTION_LENGTH - state.tagDescription.length} characters left`
            }
            onFocus={() => setState({ onFocusedInput: 'description' })}
            onBlur={() => setState({ onFocusedInput: undefined })}
            value={state.tagDescription}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setState({
                tagDescription: e.target.value.substring(0, DESCRIPTION_LENGTH),
              })
            }
          />
        </Box>
      </PopupDialog>

      {/* Delete Tag Modal */}
      <PopupDialog
        open={state.showDeleteTagModal}
        title="Delete Scenario Tag?"
        close={handleDeleteTagModalClose}
        isLoading={state.isModalLoading}
        primaryButtons={[
          {
            id: 'delete-tag-button',
            label: 'Yes, delete it',
            onClick: handleDeleteTag,
            disabled:
              state.isModalLoading ||
              state.deleteConfirmationName !== state.selectedTag?.name,
          },
        ]}
      >
        <Typography>
          This action <strong>cannot</strong> be undone. Are you absolutely sure
          you want to delete "<strong>{state.selectedTag?.name}</strong>"?
        </Typography>

        <TextField
          fullWidth
          value={state.deleteConfirmationName}
          label="Please type the name of this tag to confirm."
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setState({ deleteConfirmationName: e.target.value })
          }
          sx={{ mt: 2 }}
        />

        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: 'warning.lighter',
            borderLeft: 3,
            borderColor: 'warning.main',
            borderRadius: 1,
          }}
        >
            <Typography variant="body2" color="text.secondary">
            <strong>Note:</strong> Once deleted, all scenarios currently associated with this tag will be automatically unlinked.
            </Typography>
        </Box>
      </PopupDialog>

      {/* Main Dialog */}
      <PopupDialog
        title="Manage Scenario Tags"
        open={props.isManageScenarioTagsDialogOpen}
        close={() => {
          props.handleCloseManageScenarioTagsDialog();
          setState(initialState);
        }}
        maxWidth={width > 1600 ? 'lg' : 'xl'}
        hideDialogActions
      >
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Grid item md={9}></Grid>
          <Grid item md={3} container justifyContent="flex-end" spacing={1}>
            <Grid item>
              <Button
                onClick={loadTags}
                variant="outlined"
                size="small"
                disabled={state.isDataLoading}
              >
                Refresh
              </Button>
            </Grid>
            <Grid item>
              <Button
                onClick={handleAddTagModalOpen}
                variant="contained"
                size="small"
                disabled={state.isDataLoading}
              >
                Add Tag
              </Button>
            </Grid>
          </Grid>
        </Grid>

        {state.isDataLoading ? (
          <CenteredSpinner />
        ) : state.scenarioTags.length > 0 ? (
          <Table
            id="scenario-tags-table"
            isLoading={state.isDataLoading}
            isSuccess
            pagination={true}
            rowCount={state.scenarioTags.length}
            rowData={state.scenarioTags}
            columnDefs={scenarioTagsColumns}
            defaultColDef={{
              suppressAutoSize: true,
              suppressMenu: true,
              suppressSizeToFit: true,
            }}
          />
        ) : (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No scenario tags found. Click "Add Tag" to create one.
            </Typography>
          </Box>
        )}
      </PopupDialog>
    </>
  );
};

export default ManageScenarioTags;
